import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import { useUser } from './UserContext';

const SalesMembersContext = createContext(null);

export function SalesMembersProvider({ children }) {
  const { user } = useUser();
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMembers = useCallback(async () => {
    if (!user) {
      setAllMembers([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/sales-members/');
      if (!res.ok) {
        throw new Error('営業メンバーの取得に失敗しました');
      }
      const data = await res.json();
      setAllMembers(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      setError(err.message || '営業メンバーの取得に失敗しました');
      setAllMembers([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const ppMembers = useMemo(
    () => allMembers.filter((member) => member.pp_enabled),
    [allMembers],
  );

  const bpMembers = useMemo(
    () => allMembers.filter((member) => member.bp_enabled),
    [allMembers],
  );

  return (
    <SalesMembersContext.Provider
      value={{
        allMembers,
        ppMembers,
        bpMembers,
        loading,
        error,
        refreshMembers: fetchMembers,
      }}
    >
      {children}
    </SalesMembersContext.Provider>
  );
}

export function useSalesMembers() {
  const context = useContext(SalesMembersContext);
  if (!context) {
    throw new Error('useSalesMembers must be used within SalesMembersProvider');
  }
  return context;
}
